import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QUIZ_QUESTIONS, HATE_REASONS_TO_KEYS, PREF_KEYS, OPTION_DESC } from "@/lib/quiz";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { loadProgress, syncProgress, setLocalProgress } from "@/lib/progress";
import { toast } from "sonner";
import { trackQuiz } from "@/lib/analytics";
import { TopPathReveal } from "@/components/TopPathReveal";

const DEFAULT_ANS = { hate_level: 7, hate_reasons: [], preferences: [] };

export default function Quiz() {
  const { user, loading } = useAuth();
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState(DEFAULT_ANS);
  const [ready, setReady] = useState(false);
  const [reveal, setReveal] = useState(null);
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const retake = sp.get("retake") === "1";
  const q = QUIZ_QUESTIONS[step];
  const total = QUIZ_QUESTIONS.length;

  // Resume exactly where they left off (server first when signed in, else local).
  useEffect(() => {
    if (loading) return;
    let alive = true;
    (async () => {
      if (user) {
        try {
          const { data } = await api.get("/resume");
          if (!alive) return;
          if (data.quiz_completed && !retake) { nav("/dashboard", { replace: true }); return; }
        } catch {}
      }
      const p = await loadProgress();
      if (!alive) return;
      if (p.answers && typeof p.answers === "object") setAns({ ...DEFAULT_ANS, ...p.answers });
      if (retake) setStep(0);
      else if (typeof p.step === "number") setStep(Math.max(0, Math.min(p.step, total - 1)));
      setReady(true);
    })();
    return () => { alive = false; };
  }, [total, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ready || user) return; // funnel counts new visitors only, not retakes
    if (step === 0) trackQuiz("start");
    else trackQuiz("step", step);
  }, [step, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save every answer the moment it changes (debounced).
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!ready) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      syncProgress({ answers: ans, step, last_route: "/quiz" });
    }, 350);
    return () => clearTimeout(saveTimer.current);
  }, [ans, step, ready]);

  const setValue = (v) => setAns(a => ({ ...a, [q.id]: v }));
  const toggle = (v, key) => {
    setAns(a => {
      const list = a[q.id] || [];
      const has = list.includes(key ?? v);
      return { ...a, [q.id]: has ? list.filter(x => x !== (key ?? v)) : [...list, (key ?? v)] };
    });
  };

  const next = async () => {
    if (step < total - 1) return setStep(step + 1);
    const payload = {
      ...ans,
      hate_reasons: (ans.hate_reasons || []).map(r => HATE_REASONS_TO_KEYS[r] || r),
      preferences: (ans.preferences || []).map(p => PREF_KEYS[p] || p),
      target_income: parseFloat(ans.target_income) || null,
    };
    if (!user) {
      trackQuiz("finish");
      // Keep answers, attach to the account right after they sign up. Show their #1 path first.
      setLocalProgress({ pending_quiz: payload, answers: ans, step: total - 1 });
      try {
        const { data } = await api.post("/quiz/preview", payload, { timeout: 20000 });
        if (data.top) { setReveal(data); window.scrollTo(0, 0); return; }
      } catch { /* fall through to signup; answers are kept */ }
      nav("/auth?next=/dashboard");
      return;
    }
    setLocalProgress({ pending_quiz: payload, answers: ans, step: total - 1 });
    try {
      await api.post("/quiz", payload, { timeout: 20000 });
      setLocalProgress({ pending_quiz: null });
      syncProgress({ answers: ans, step: total, last_route: "/dashboard" });
      toast.success("Locked in. Finding your paths…");
      nav("/dashboard");
    } catch (e) {
      toast.error("That took too long. Your answers are saved, tap Finish to try again.");
    }
  };

  const prev = () => step > 0 && setStep(step - 1);

  const renderQuestion = () => {
    if (q.type === "slider") {
      const v = ans[q.id] ?? q.default;
      return (
        <div>
          <input type="range" min={q.min} max={q.max} value={v} onChange={e => setValue(parseInt(e.target.value))} className="w-full accent-[#FD0F10]" data-testid={`quiz-${q.id}`} />
          <div className="flex justify-between text-xs font-mono-ui text-gray-400 mt-2">
            <span>{q.min} · {q.labels[0]}</span><span>{q.max} · {q.labels[1]}</span>
          </div>
          <div className="text-center mt-4 font-display text-5xl font-black text-[#FD0F10]">{v}</div>
        </div>
      );
    }
    if (q.type === "single") {
      return (
        <div className="space-y-2">
          {q.options.map(o => {
            const val = typeof o === "string" ? o : o.value;
            const label = typeof o === "string" ? o : o.label;
            const active = ans[q.id] === val;
            return (
              <button key={val} onClick={() => setValue(val)} className={`w-full text-left p-4 rounded-lg border transition ${active ? "border-[#FD0F10] bg-[#FD0F10]/10 text-white" : "border-[#2A2E39] bg-[#14161B] text-gray-200 hover:border-[#FD0F10]/50"}`} data-testid={`quiz-option-${val}`}>
                {label}
                {o.recommended && <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#FD0F10]/15 text-[#FD0F10]" data-testid={`quiz-option-recommended-${val}`}>Recommended</span>}
                {o.desc && <span className="block text-xs text-gray-400 mt-1 font-normal" data-testid={`quiz-option-desc-${val}`}>{o.desc}</span>}
              </button>
            );
          })}
        </div>
      );
    }
    if (q.type === "multi") {
      const list = ans[q.id] || [];
      return (
        <div className="grid grid-cols-2 gap-2">
          {q.options.map(o => {
            const active = list.includes(o);
            return (
              <button key={o} onClick={() => toggle(o)} className={`p-3.5 rounded-lg border text-left text-sm transition ${active ? "border-[#FD0F10] bg-[#FD0F10]/10 text-white" : "border-[#2A2E39] bg-[#14161B] text-gray-200 hover:border-[#FD0F10]/50"}`} data-testid={`quiz-multi-${o.replace(/\s+/g,'-')}`}>
                {o}
                {OPTION_DESC[o] && <span className="block text-[11px] leading-snug text-gray-400 mt-1">{OPTION_DESC[o]}</span>}
              </button>
            );
          })}
        </div>
      );
    }
    if (q.type === "text") {
      return <textarea value={ans[q.id] || ""} onChange={e => setValue(e.target.value)} rows={4} placeholder={q.placeholder} className="w-full bg-[#14161B] border border-[#2A2E39] rounded-lg px-4 py-3 focus:border-[#FD0F10] outline-none" data-testid={`quiz-${q.id}`} />;
    }
    if (q.type === "number") {
      return <input type="number" value={ans[q.id] || ""} onChange={e => setValue(e.target.value)} placeholder={q.placeholder} className="w-full bg-[#14161B] border border-[#2A2E39] rounded-lg px-4 py-3 focus:border-[#FD0F10] outline-none text-xl font-mono-ui" data-testid={`quiz-${q.id}`} />;
    }
  };

  const answered = q.type === "single" ? ans[q.id] != null && ans[q.id] !== ""
    : q.type === "multi" ? (ans[q.id] || []).length > 0 : true;

  if (reveal) return <TopPathReveal top={reveal.top} total={reveal.total} />;

  return (
    <div className="max-w-2xl mx-auto px-5 pt-8">
      {step === 0 && (
        <div className="mb-6 card-panel p-4" data-testid="quiz-intro">
          <div className="eyebrow text-[#FD0F10]">BEFORE WE START</div>
          <div className="mt-2 text-sm sm:text-base text-gray-100 leading-relaxed space-y-0.5">
            <p>Answer honestly.</p>
            <p>Not the interview version.</p>
            <p>The real one.</p>
            <p className="pt-2 text-white font-bold">Better answers. Better plan.</p>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between eyebrow" data-testid="quiz-progress">
        <span>STEP {step + 1} / {total}</span>
        <span>{Math.round(((step + 1) / total) * 100)}%</span>
      </div>
      <div className="h-1 bg-[#2A2E39] rounded-full mt-2 overflow-hidden">
        <div className="h-full hero-accent transition-all" style={{ width: `${((step + 1) / total) * 100}%` }} />
      </div>
      <h2 className="mt-8 font-display text-2xl sm:text-3xl font-extrabold leading-tight" data-testid="quiz-question">{q.title}</h2>
      {q.hint && <p className="mt-2 text-sm text-gray-300" data-testid="quiz-hint">{q.hint}</p>}
      <div className="mt-6">{renderQuestion()}</div>
      {q.note && <p className="mt-3 text-xs text-gray-400 leading-relaxed" data-testid="quiz-note">{q.note}</p>}
      {q.smallprint && <p className="mt-2 text-[11px] text-gray-500 leading-relaxed" data-testid="quiz-smallprint">{q.smallprint}</p>}
      <div className="mt-8 flex gap-3 sticky bottom-[calc(5rem+env(safe-area-inset-bottom))] sm:static bg-[#0B0C0E]/95 py-2 sm:py-0">
        {step > 0 && (
          <button onClick={prev} className="px-5 py-3 border border-[#2A2E39] rounded-lg text-gray-300 hover:border-white" data-testid="quiz-back">Back</button>
        )}
        <button onClick={next} disabled={!ready || !answered} className="flex-1 disabled:opacity-60 hero-accent text-white font-display font-bold text-lg py-3.5 rounded-lg" data-testid="quiz-next">
          {step === total - 1 ? "BUILD MY ESCAPE PLAN" : "Next"}
        </button>
      </div>
      {!answered && <p className="mt-3 text-xs text-gray-400 text-center" data-testid="quiz-pick-hint">Pick an answer to continue.</p>}
      {step === total - 1 && (
        <p className="mt-4 text-xs text-gray-400 text-center">One tap. Your plan is ready.</p>
      )}
    </div>
  );
}
